import { useDynamicForm } from "@/components/dynamic-form/hook/useDynamicForm";
import { enhanceEndTime } from "@/components/dynamic-form/formatter/enhanceEndTime";
import { useWatchPagination } from "@/components/dynamic-form/hook/useWatchPagination";
import { DynamicForm } from "@/components/dynamic-form";
import { getInitialValues } from "@/components/dynamic-form/helper/getInitialValues";
import { useSearchConfig } from "@/pages/consumption/view/billing-details/view/volume-detail/hooks/useSearchConfig";
import { Table, Popover, Dropdown } from "@renderbus/ui";
import { useTableColumns } from "@/pages/consumption/view/billing-details/view/volume-detail/hooks/useTableColumns";
import { useCustomColumns } from "project-config/hooks/useCustomColumns";
import { useFilterColumns } from "project-config/hooks/useFilterColumns";
import {
  OperationArea,
  SearchAreaWrapper,
} from "@/pages/consumption/components/atom";
import { ColumnCheckboxes } from "@/pages/consumption/components/column-checkboxes";
import { OperationButton } from "@/components/operation-button";
import { useTranslation } from "react-i18next";
import { useConsumeStore } from "@/pages/consumption/store/ConsumeStore";
import { useExportBill } from "@/pages/consumption/hooks/useExportBill";
import { Modal } from "@/components/renderbus-modal";
import { ExportBillForm } from "@/pages/consumption/components/export-form/export-bill-form";
import { ExportTaskFrameBillForm } from "@/pages/consumption/components/export-form/export-task-frame-bill-form";
import { useGlobalStore } from "@/store/GlobalStore";
import { useAccountList } from "@/pages/consumption/view/billing-details/view/volume-detail/hooks/useAccountList";
import { getDefaultExportName } from "@/pages/consumption/helper/getDefaultExportName";
import routerUtil from "@/utils/routerUtil";
import { useEffect } from "react";
import styled, { createGlobalStyle } from "styled-components";
import {
  BillDetailsTabsKey,
  BillTabTypeEnum,
} from "@/pages/consumption/constants";
import { Whether } from "project-config/constants";
import DownOutlinedIcon from "@/assets/icons/profile/down-outlined";
import { Button } from "@renderbus/ui";
import { useToastStore } from "@/store/toast-store";

export function VolumeDetail() {
  const { userInfo, isSubAccount, is88StudioUser } = useGlobalStore();
  const {
    form,
    getSearchParams,
    tableData,
    fetchData,
    pageNum,
    setPageNum,
    total,
    pageSize,
  } = useDynamicForm({
    api: "/api/rendering/user/consume/bill/getUserBillDetails",
    defaultPageSize: 10,
    formatter: {
      time: enhanceEndTime,
      taskIds: (v) => (v ? v.split(",") : undefined),
    },
  });

  const { fetchBillConsume } = useConsumeStore();
  const { showGlobalToast } = useToastStore();

  const { data: accountList } = useAccountList();

  const fetch = async (isSearch = false) => {
    if (isSearch) {
      setPageNum(1);
    }
    console.log("getSearchParams", getSearchParams());
    const taskIds = form.getFieldValue("taskIds");
    if (taskIds) {
      if (!/^[0-9,]+$/.test(taskIds)) {
        showGlobalToast({
          type: "error",
          content: t("tasks_id_error"),
        });
        return;
      }
    }
    fetchBillConsume(getSearchParams()).then();
    fetchData().then();
  };

  useWatchPagination(pageSize, pageNum, fetch);

  const { searchConfig } = useSearchConfig();
  const is88StudioSubAccount = is88StudioUser && isSubAccount;
  const { columns } = useTableColumns(is88StudioSubAccount as boolean);

  const { t } = useTranslation("center");
  const { t: consumptionT } = useTranslation("consumption");

  const defaultUnchecked = [
    "layerName",
    "renderCamera",
    "framesRange",
    "averageDuration",
    "graphicsCardsNum",
    "taskRam",
    "model",
    "organizationStructure",
  ];
  const { checkedValue, onChange, defaultCheckData } = useCustomColumns(
    columns,
    "consumption_task_tab",
    { defaultUnchecked }
  );

  const { columns: newColumns } = useFilterColumns(columns, checkedValue);

  const searchParams = getSearchParams({ hiddenPageSize: true });
  const {
    exportForm,
    openModal,
    setModal,
    onOkTask,
    exportFrameForm,
    openFrameModal,
    setFrameModal,
    onFrameOk,
    exportHardwareForm,
    openHardwareModal,
    setHardwareModal,
    onHardwareOk,
    handleExportFrameBill,
    openTaskFrameModal,
    setTaskFrameModal,
    exportTaskFrameForm,
    onTaskFrameOk,
  } = useExportBill({
    api: "/api/rendering/task/export/exportUserBillDetails",
    frameApi: "/api/rendering/task/export/exportFrameConsumeList",
    hardwareApi: "/api/rendering/task/export/exportUserBillByHardware",
    taskFrameApi: "/api/rendering/task/export/exportFrameAndTaskWiseReport",
    searchParams,
  });

  const { tip, activeKey, billDetailsActiveKey } = useConsumeStore();

  useEffect(() => {
    if (
      activeKey === BillTabTypeEnum.BillingDetails &&
      billDetailsActiveKey === BillDetailsTabsKey.VolumeDetail
    ) {
      fetch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeKey, billDetailsActiveKey]);

  return (
    <>
      <SearchAreaWrapper>
        <DynamicForm
          initialValues={getInitialValues(searchConfig)}
          searchConfig={searchConfig}
          searchForm={form}
          onSearch={() => fetch(true)}
        />
      </SearchAreaWrapper>
      <TableAbove>
        <span style={{ textAlign: "left" }}>{tip}</span>
        <OperationArea>
          <Popover
            content={
              <ColumnCheckboxes
                columns={columns.filter((item) => Object.keys(item).length > 0)}
                onChange={onChange}
                value={checkedValue}
                defaultValue={[...defaultCheckData, ...defaultUnchecked]}
              />
            }
          >
            <>
              <OperationButton text={t("consumption_custom_column")} />
            </>
          </Popover>
          <Dropdown
            dropdownRender={() => (
              <ButtonWrapper onClick={() => {}}>
                <TextButton type="text" onClick={() => setModal(true)}>
                  {consumptionT("consumption_exprot_task_bill")}
                </TextButton>
                {Boolean(userInfo?.exportFrameConsume) && (
                  <TextButton
                    type="text"
                    onClick={() =>
                      handleExportFrameBill(form.getFieldValue("taskIds"))
                    }
                  >
                    {consumptionT("consumption_exprot_frame_bill")}
                  </TextButton>
                )}
                <TextButton type="text" onClick={() => setHardwareModal(true)}>
                  {consumptionT("consumption_exprot_hardware_bill")}
                </TextButton>
                {is88StudioUser && (
                  <TextButton
                    type="text"
                    onClick={() => setTaskFrameModal(true)}
                  >
                    {consumptionT("consumption_export_task_frame_bill")}
                  </TextButton>
                )}
              </ButtonWrapper>
            )}
            overlayClassName="export-child-more-dropdown"
          >
            <DropdownReference>
              {t("consumption_export")}
              <DownOutlinedIcon />
            </DropdownReference>
          </Dropdown>
        </OperationArea>
      </TableAbove>
      <Table
        paginationCurrent={pageNum}
        columns={[...newColumns]}
        dataSource={tableData}
        gap={false}
        total={total}
        defaultPageSize={pageSize}
        onPageNumChange={async (num: number) => {
          setPageNum(num);
        }}
      />
      {userInfo?.oldConsumptionBill === Whether.Yes && (
        <OperationButton
          width="max-content"
          text={t("consumption_check_previous_consumption")}
          onClick={() => routerUtil.navigate?.("consumption/previous")}
        />
      )}

      <MoreButtonDropdownStyle />

      <Modal
        className={`renderbus-ui--modal`}
        title={consumptionT("consumption_exprot_task_bill")}
        open={openModal}
        destroyOnClose
        onOk={() => {
          onOkTask();
        }}
        onCancel={() => {
          setModal(false);
        }}
      >
        <ExportBillForm
          form={exportForm}
          defaultExportName={getDefaultExportName(
            searchParams,
            accountList ?? [],
            "task"
          )}
        />
      </Modal>

      <Modal
        className={`renderbus-ui--modal`}
        title={consumptionT("consumption_exprot_frame_bill")}
        open={openFrameModal}
        destroyOnClose
        onOk={() => {
          onFrameOk();
        }}
        onCancel={() => {
          setFrameModal(false);
        }}
      >
        <ExportBillForm
          form={exportFrameForm}
          defaultExportName={getDefaultExportName(
            searchParams,
            accountList ?? [],
            "frame"
          )}
        />
      </Modal>

      <Modal
        className={`renderbus-ui--modal`}
        title={consumptionT("consumption_exprot_hardware_bill")}
        open={openHardwareModal}
        destroyOnClose
        onOk={() => {
          onHardwareOk();
        }}
        onCancel={() => {
          setHardwareModal(false);
        }}
      >
        <ExportBillForm
          form={exportHardwareForm}
          defaultExportName={getDefaultExportName(
            searchParams,
            accountList ?? [],
            "hardware"
          )}
        />
        <TipContent>
          <p>{consumptionT("consumption_exprot_hardware_bill_tip1")}</p>
          <p>{consumptionT("consumption_exprot_hardware_bill_tip2")}</p>
          <p>{consumptionT("consumption_exprot_hardware_bill_tip3")}</p>
          <p>{consumptionT("consumption_exprot_hardware_bill_tip4")}</p>
        </TipContent>
      </Modal>
      <Modal
        className={`renderbus-ui--modal`}
        title={consumptionT("consumption_export_task_frame_bill")}
        open={openTaskFrameModal}
        destroyOnClose
        onOk={() => {
          onTaskFrameOk();
        }}
        onCancel={() => {
          setTaskFrameModal(false);
        }}
      >
        <ExportTaskFrameBillForm form={exportTaskFrameForm} />
      </Modal>
    </>
  );
}

const TableAbove = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 5px;
  margin-bottom: -10px;
`;

const ButtonWrapper = styled.div`
  top: 100%;
  display: flex;
  flex-direction: column;
  background: #333336;
  border-radius: 2px;
  z-index: 1;
  padding: 5px 0;
  button {
    width: 100%;
    padding: 5px 8px 5px 12px;
    text-align: start;
    color: rgba(255, 255, 255, 0.85);
    &:hover {
      background: rgba(19, 203, 117, 0.1);
      color: var(--main-color);
    }
  }
`;
const DropdownReference = styled.div`
  display: flex;
  align-items: center;
  color: var(--main-color);
  &:hover {
    > svg {
      transform: rotate(180deg);
      animation: 0.5s all;
    }
  }
`;
const MoreButtonDropdownStyle = createGlobalStyle`
  .export-child-more-dropdown {
    .ant-dropdown-menu {
      background: transparent;
      border-radius: 4px;
      padding: unset !important;
    }
    .ant-dropdown-menu-item {
      padding: unset !important;
      &:hover {
        background: red !important;
        color: green !important;
      }
    }
  }
`;

const TextButton = styled(Button)`
  width: min-content;
  padding: 0;
  margin-right: 16px;
  color: var(--main-color) !important;
  border: none;
  display: flex;
  align-items: center;
  &:hover {
    color: #13cb75 !important;
  }
`;

const TipContent = styled.div`
  width: 400px;
  margin: auto;
`;
